import { CONTENT_TYPES } from 'dashboard/components-next/message/constants';
import { useCallsStore } from 'dashboard/stores/calls';
import WhatsappVoiceClient from 'dashboard/api/channel/voice/whatsappVoiceClient';
import types from 'dashboard/store/mutation-types';

export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  'no-answer',
  'canceled',
  'missed',
  'ended',
];

// Statuses that mean the call was picked up by someone — should dismiss from other agents
const ANSWERED_STATUSES = ['in-progress'];

export const isInbound = direction => direction === 'inbound';

const isVoiceCallMessage = message => {
  return CONTENT_TYPES.VOICE_CALL === message?.content_type;
};

const shouldSkipCall = (callDirection, senderId, currentUserId) => {
  return callDirection === 'outbound' && senderId !== currentUserId;
};

function extractCallData(message) {
  const contentData = message?.content_attributes?.data || {};
  const sender = message?.sender || {};
  return {
    callSid: contentData.call_sid,
    status: contentData.status,
    callDirection: contentData.call_direction,
    conversationId: message?.conversation_id,
    senderId: sender.id,
    senderName: sender.name,
    senderPhone: sender.phone_number,
    senderAvatar: sender.avatar || sender.thumbnail,
    webrtcPayload: contentData.webrtc_payload,
  };
}

/**
 * Checks if the current user should see the incoming call based on
 * the conversation's assigned agent and team:
 * - If assigned to a specific agent → only that agent sees it
 * - If assigned to a team (but no agent) → only team members see it
 * - If no agent and no team → all agents see it
 */
function shouldShowCallToUser(message, rootGetters) {
  if (!rootGetters) return true;

  const conversationId = message?.conversation_id;
  if (!conversationId) return true;

  const conversation = rootGetters.getConversationById?.(conversationId);
  if (!conversation) return true;

  const assignee = conversation.meta?.assignee;
  const team = conversation.meta?.team;
  const currentUserId = rootGetters.getCurrentUserID;

  // If conversation has an assigned agent, only they should see the call
  if (assignee?.id) {
    return assignee.id === currentUserId;
  }

  // If conversation has a team but no agent, only team members should see it
  if (team?.id) {
    const myTeams = rootGetters['teams/getMyTeams'] || [];
    return myTeams.some(t => t.id === team.id);
  }

  // No agent or team → show to all agents
  return true;
}

export function handleVoiceCallCreated(message, currentUserId, rootGetters) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    status,
    callDirection,
    conversationId,
    senderId,
    senderName,
    senderPhone,
    senderAvatar,
    webrtcPayload,
  } = extractCallData(message);
  const inboxId = message?.inbox_id;

  // Terminal statuses from new messages: clean up the call store
  if (TERMINAL_STATUSES.includes(status)) {
    const callsStore = useCallsStore();
    callsStore.handleCallStatusChanged({ callSid, status, conversationId });
    return;
  }

  // For outbound calls: if this message contains the SDP answer from the Connect webhook,
  // apply it to the existing peer connection. This happens when the backend creates a
  // new message instead of updating the existing one.
  if (webrtcPayload && callDirection === 'outbound') {
    const callsStore = useCallsStore();
    const existingCall = callsStore.calls.find(c => c.callSid === callSid);
    // eslint-disable-next-line no-console
    console.log(
      `[WhatsApp Call] SDP answer in message.created for outbound call ${callSid}`,
      { hasExistingCall: !!existingCall, isActive: existingCall?.isActive }
    );
    if (existingCall && !existingCall.isActive) {
      WhatsappVoiceClient.applyRemoteAnswer(webrtcPayload).then(success => {
        // eslint-disable-next-line no-console
        console.log(
          `[WhatsApp Call] applyRemoteAnswer result: ${success}`,
          callSid
        );
        if (success) {
          callsStore.setCallActive(callSid);
        }
      });
    }
    return;
  }

  if (shouldSkipCall(callDirection, senderId, currentUserId)) return;
  if (!shouldShowCallToUser(message, rootGetters)) return;

  const callsStore = useCallsStore();
  callsStore.addCall({
    callSid,
    conversationId,
    callDirection,
    senderId,
    senderName,
    senderPhone,
    senderAvatar,
    inboxId,
    webrtcPayload,
  });
}

export function handleVoiceCallUpdated(
  commit,
  message,
  currentUserId,
  rootGetters
) {
  if (!isVoiceCallMessage(message)) return;

  const {
    callSid,
    status,
    callDirection,
    conversationId,
    senderId,
    senderName,
    senderPhone,
    senderAvatar,
    webrtcPayload,
  } = extractCallData(message);
  const inboxId = message?.inbox_id;

  const callsStore = useCallsStore();

  // For outbound calls: when the Connect webhook arrives with an SDP answer,
  // apply it to the existing peerConnection to complete the WebRTC handshake
  if (webrtcPayload && callDirection === 'outbound') {
    const existingCall = callsStore.calls.find(c => c.callSid === callSid);
    // eslint-disable-next-line no-console
    console.log(
      `[WhatsApp Call] SDP answer received for outbound call ${callSid}`,
      { hasExistingCall: !!existingCall, isActive: existingCall?.isActive }
    );
    if (existingCall && !existingCall.isActive) {
      WhatsappVoiceClient.applyRemoteAnswer(webrtcPayload).then(success => {
        // eslint-disable-next-line no-console
        console.log(
          `[WhatsApp Call] applyRemoteAnswer result: ${success}`,
          callSid
        );
        if (success) {
          callsStore.setCallActive(callSid);
        }
      });
    }
  }

  // When an inbound call is answered (in-progress), dismiss from agents who haven't joined.
  // Outbound calls are NOT dismissed here — the agent who placed the call keeps it.
  if (ANSWERED_STATUSES.includes(status) && callDirection !== 'outbound') {
    const existingCall = callsStore.calls.find(c => c.callSid === callSid);
    if (existingCall && !existingCall.isActive) {
      callsStore.dismissCall(callSid);
    }
  }

  callsStore.handleCallStatusChanged({ callSid, status, conversationId });

  const callInfo = { conversationId, callStatus: status };
  commit(types.UPDATE_CONVERSATION_CALL_STATUS, callInfo);
  commit(types.UPDATE_MESSAGE_CALL_STATUS, callInfo);

  const isNewCall =
    status === 'ringing' &&
    !shouldSkipCall(callDirection, senderId, currentUserId) &&
    shouldShowCallToUser(message, rootGetters);

  if (isNewCall) {
    callsStore.addCall({
      callSid,
      conversationId,
      callDirection,
      senderId,
      senderName,
      senderPhone,
      senderAvatar,
      inboxId,
      webrtcPayload,
    });
  }
}
